import { Component, EventEmitter, Input, OnChanges, Output, inject } from "@angular/core";
import { FormBuilder, FormControl, ReactiveFormsModule } from "@angular/forms";
import {
  FS_SUGGESTED_CARRY_PCNT,
  FS_SUGGESTED_EXPECTED_EXPENSES,
  FS_SUGGESTED_TOTAL_COMMITMENT,
} from "../pojo/fund-strategy-constants";
import { FundStrategyMetricsNames } from "../pojo/fund-strategy-enums";
import {
  FsSettingsData,
  FSFundMetricsDTO,
  FSFundMetricsMiniDTO,
  FundStrategyModel, TotalsCompaniesNumberFS,
} from "../pojo/fund-strategy-export-classes";
import { FSFundMetricsGroup } from "../pojo/fund-strategy-export-types";
import { FundStrategyService } from "../service/fund-strategy-service";
import { FpModifyValuePipe } from "../../../../util/pipes/fp-modify-value.pipe";
import { InputComponent } from "../../../shared/component/inputs/input-component/input.component";

@Component({
  selector: 'app-fund-strategy-settings-inputs',
  templateUrl: './fund-strategy-settings-inputs.component.html',
  styleUrls: ['./fund-strategy-settings-inputs.component.scss'],
  imports: [
    FpModifyValuePipe,
    InputComponent,
    ReactiveFormsModule,
  ],
})

export class FundStrategySettingsInputsComponent implements OnChanges {
  private fb = inject(FormBuilder);
  private fundStrategyService = inject(FundStrategyService);

  @Input({ required: true }) metricsData = new FsSettingsData();
  @Input({ required: true }) fundStrategy: FundStrategyModel;
  @Input({ required: true }) isModelBlocked = false;
  @Output() inputsSaveEmitter: EventEmitter<FSFundMetricsMiniDTO> = new EventEmitter<FSFundMetricsMiniDTO>();

  metrics = {
    name: FundStrategyMetricsNames,
  };

  metricsForm: FSFundMetricsGroup;
  companiesNumbers = new TotalsCompaniesNumberFS();


  ngOnChanges(): void {
    if (this.isModelBlocked) {
      this.metricsData = new FSFundMetricsDTO();
      this.metricsData.totalCommitment = FS_SUGGESTED_TOTAL_COMMITMENT;
      this.metricsData.expectedExpenses = FS_SUGGESTED_EXPECTED_EXPENSES;
      this.metricsData.carryInterestPcnt = FS_SUGGESTED_CARRY_PCNT * 100;
    }
    this.initMetricsForm(this.metricsData);
    this.calculateCompaniesNumber();
  }

  calculateMetrics(value: number, metric: FundStrategyMetricsNames): void {
    const numberValue = Number(value);
    const metricsData = new FSFundMetricsDTO(
      Number(this.metricsForm.getRawValue().totalCommitment),
      Number(this.metricsForm.getRawValue().expectedExpenses),
      Number(this.metricsForm.getRawValue().carryInterestPcnt),
      Number(this.metricsForm.getRawValue().investment),
      Number(this.metricsForm.getRawValue().proceeds),
    );

    switch (metric) {
      case FundStrategyMetricsNames.totalCommitment:
        metricsData.totalCommitment = numberValue;
        const unAllocatedCommitment = metricsData.unAllocatedCommitment;

        this.metricsForm.controls.investmentPcnt.patchValue(numberValue ? Math.abs(this.metricsForm.getRawValue().investment / numberValue * 100) : 0);
        this.metricsForm.controls.unAllocatedCommitmentPcnt.patchValue(numberValue ? Math.abs(unAllocatedCommitment / numberValue * 100) : 0);
        break;
      case FundStrategyMetricsNames.expectedExpenses:
        metricsData.expectedExpenses = numberValue;
        this.metricsForm.controls.expectedExpensesPcnt.patchValue(metricsData.expectedExpensesPcnt, { emitEvent: false });
        this.metricsForm.controls.unAllocatedCommitmentPcnt.patchValue(Number(metricsData.totalCommitment) ? Math.abs(metricsData.unAllocatedCommitment / Number(metricsData.totalCommitment) * 100) : 0);
        break;
      case FundStrategyMetricsNames.expectedExpensesPcnt:
        this.metricsForm.controls.expectedExpenses.patchValue((numberValue * metricsData.totalCommitment) / 100, { emitEvent: false });
        metricsData.expectedExpenses = this.metricsForm.getRawValue().expectedExpenses;
        this.metricsForm.controls.unAllocatedCommitmentPcnt.patchValue(Number(metricsData.totalCommitment) ? Math.abs(metricsData.unAllocatedCommitment / Number(metricsData.totalCommitment) * 100) : 0);
        break;
      case FundStrategyMetricsNames.carryInterest:
        const netGain = metricsData.netGain;
        metricsData.carryInterestPcnt = netGain ? (numberValue / netGain) * 100 : 0;
        this.metricsForm.controls.carryInterestPcnt.patchValue(metricsData.carryInterestPcnt, { emitEvent: false });
        break;
      case FundStrategyMetricsNames.carryInterestPcnt:
        metricsData.carryInterestPcnt = numberValue;
        this.metricsForm.controls.carryInterest.patchValue(metricsData.carryInterest, { emitEvent: false });
        break;
    }

    this.metricsForm.patchValue({
      tvpi: metricsData.tvpi,
      netGain: metricsData.netGain,
      unAllocatedCommitment: metricsData.unAllocatedCommitment,
      netGainAfterCarry: metricsData.netGainAfterCarry,
      fundMultiple: metricsData.fundMultiple,
    });

    if (metricsData.netGain < 0) {
      this.metricsForm.controls.carryInterest.patchValue(0, { emitEvent: false });
      this.metricsForm.controls.carryInterestPcnt.patchValue(0, { emitEvent: false });
      this.metricsForm.controls.carryInterest.disable({ emitEvent: false });
      this.metricsForm.controls.carryInterestPcnt.disable({ emitEvent: false });
    } else {
      this.metricsForm.controls.carryInterest.enable({ emitEvent: false });
      this.metricsForm.controls.carryInterestPcnt.enable({ emitEvent: false });
    }
  }

  emitSaveInputs(): void {
    const metricsForm = this.metricsForm.getRawValue();
    const miniDto = new FSFundMetricsMiniDTO(
      metricsForm.totalCommitment,
      metricsForm.expectedExpenses,
      metricsForm.carryInterestPcnt / 100,
    );
    this.inputsSaveEmitter.emit(miniDto);
  }

  initMetricsForm(metricsData: FSFundMetricsDTO): void {
    this.metricsForm = this.fb.group({
      totalCommitment: new FormControl({ value: metricsData.totalCommitment, disabled: this.isModelBlocked }),
      investment: new FormControl({ value: metricsData.investment, disabled: true }),
      investmentPcnt: new FormControl(Math.abs(metricsData.investment / metricsData.totalCommitment * 100)),
      expectedExpenses: new FormControl({ value: metricsData.expectedExpenses, disabled: this.isModelBlocked }),
      expectedExpensesPcnt: new FormControl({ value: metricsData.expectedExpensesPcnt, disabled: this.isModelBlocked }),
      unAllocatedCommitment: new FormControl({ value: metricsData.unAllocatedCommitment, disabled: true }),
      unAllocatedCommitmentPcnt: new FormControl(Math.abs(metricsData.unAllocatedCommitment / metricsData.totalCommitment * 100)),
      portfolioGain: new FormControl({ value: metricsData.portfolioGain, disabled: true }),
      netGain: new FormControl({ value: metricsData.netGain, disabled: true }),
      carryInterest: new FormControl({ value: metricsData.carryInterest, disabled: this.isModelBlocked || metricsData.netGain < 0 }),
      carryInterestPcnt: new FormControl({ value: metricsData.carryInterestPcnt, disabled: this.isModelBlocked || metricsData.netGain < 0 }),
      netGainAfterCarry: new FormControl({ value: metricsData.netGainAfterCarry, disabled: true }),

      moic: new FormControl(metricsData.moic),
      fundMultiple: new FormControl(metricsData.fundMultiple),
      tvpi: new FormControl(metricsData.tvpi),
      proceeds: new FormControl(metricsData.proceeds),
    });
  }

  calculateCompaniesNumber(): void {
    this.companiesNumbers = this.fundStrategyService.calculateCompaniesNumbers(this.fundStrategy);
  }

}
